import { utcFromLocal, type ZoneRules, type ZoneRulesTable } from '@/lib/time'
import type { Airport } from '@/lib/reference'
import type { FlightLeg, LegIssue, RawLeg } from './types'

/**
 * Local clock -> UTC, the join that turns a report into schedule rows.
 *
 * The file writes every time at the station it happens at, and states only a
 * departure date. Three things therefore have to be worked out rather than
 * read:
 *
 *   - the offset in force at each end, which is a function of the instant,
 *     not of the airport (DST moves it twice a year at half the network);
 *   - which day the arrival falls on, since the file never says. SGN 20:00 ->
 *     PVG 01:00 is the next morning, and CXR 22:55 -> ICN 05:35 is too;
 *   - what to do when a station is not in the airport table at all.
 *
 * The arrival day is chosen as the one giving the shortest non-negative
 * flight, which is a decision the data can always make and a human never has
 * to: a leg lands after it departs, and no leg in this operation is 20 hours
 * long. When a station is missing the block time from the file bridges the
 * gap, so an unknown airport costs the precision of one end, not the leg.
 */

/** Airports keyed by IATA. First row wins: 17 codes are claimed twice. */
export function airportIndex(airports: Airport[]): Map<string, Airport> {
  const index = new Map<string, Airport>()
  for (const airport of airports) {
    if (airport.iata && !index.has(airport.iata)) index.set(airport.iata, airport)
  }
  return index
}

/**
 * Only the column the clock needs, so a station that does not exist in the
 * table yet — one an import is about to create — can be handed in the same way
 * a stored row is.
 */
export interface StationZone {
  timezone: string
}

export interface ResolveContext {
  airportsByIata: Map<string, StationZone>
  zoneRules: ZoneRulesTable
  /** Registrations the fleet table carries, for the unknown-tail flag. */
  fleetTails: Set<string>
}

/** Longest a leg may be before the arrival day is judged implausible. */
const MAX_LEG_MINUTES = 20 * 60

export function legKey(leg: Pick<RawLeg, 'operatingDate' | 'flightNumber' | 'dep' | 'arr'>): string {
  return `${leg.operatingDate}|${leg.flightNumber}|${leg.dep}|${leg.arr}`
}

export function resolveLeg(raw: RawLeg, context: ResolveContext, batchId: string): FlightLeg {
  const issues: LegIssue[] = []
  const depRules = rulesFor(raw.dep, context)
  const arrRules = rulesFor(raw.arr, context)
  if (!depRules || !arrRules) issues.push('unknownStation')
  if (raw.regn && !context.fleetTails.has(raw.regn)) issues.push('unknownTail')

  const block = raw.blockMinutes * 60_000
  let stdUtc = depRules ? instantOf(raw.operatingDate, raw.std, depRules) : null
  let staUtc: number | null = null

  if (arrRules) {
    staUtc = arrivalInstant(raw, arrRules, stdUtc)
  }
  if (stdUtc === null && staUtc !== null && block > 0) {
    stdUtc = staUtc - block
    issues.push('blockDerived')
  }
  if (staUtc === null && stdUtc !== null) {
    // No zone for the arrival station: the file's own block time is the only
    // honest way to place the other end. Never a guessed offset.
    staUtc = stdUtc + block
    issues.push('blockDerived')
  }

  if (stdUtc === null || staUtc === null) {
    issues.push('unplaceable')
    stdUtc = 0
    staUtc = 0
  }

  return {
    key: legKey(raw),
    operatingDate: raw.operatingDate,
    flightNumber: raw.flightNumber,
    serviceType: raw.serviceType,
    regn: raw.regn,
    acType: raw.acType,
    dep: raw.dep,
    arr: raw.arr,
    stdUtc,
    staUtc,
    etdUtc: raw.etd && depRules ? nearestReading(raw, raw.etd, depRules, stdUtc) : null,
    etaUtc: raw.eta && arrRules ? nearestReading(raw, raw.eta, arrRules, staUtc) : null,
    // Verbatim, never recomputed: these are the numbers the operator wrote and
    // the numbers the shift reads back.
    stdLocal: raw.std,
    staLocal: raw.sta,
    etdLocal: raw.etd,
    etaLocal: raw.eta,
    blockMinutes: raw.blockMinutes,
    batchId,
    cancelledAt: null,
    issues,
  }
}

export function resolveLegs(raws: RawLeg[], context: ResolveContext, batchId: string): FlightLeg[] {
  return raws.map((raw) => resolveLeg(raw, context, batchId))
}

function rulesFor(iata: string, context: ResolveContext): { zone: string; rules: ZoneRules } | null {
  const airport = context.airportsByIata.get(iata)
  if (!airport?.timezone) return null
  const rules = context.zoneRules[airport.timezone]
  return rules ? { zone: airport.timezone, rules } : null
}

function instantOf(date: string, clock: string, zone: { zone: string; rules: ZoneRules }): number {
  const [year, month, day] = date.split('-').map(Number)
  const [hour, minute] = clock.split(':').map(Number)
  return utcFromLocal(zone.zone, zone.rules, { year, month, day, hour, minute })
}

/**
 * The arrival day the file leaves out.
 *
 * Same day, next day and — for a leg crossing the date line westbound — the
 * day before are all candidates; the shortest non-negative one wins.
 */
function arrivalInstant(
  raw: RawLeg,
  arrZone: { zone: string; rules: ZoneRules },
  stdUtc: number | null,
): number | null {
  if (stdUtc === null) return instantOf(raw.operatingDate, raw.sta, arrZone)

  let best: number | null = null
  for (const offset of [0, 1, -1, 2]) {
    const candidate = instantOf(addDays(raw.operatingDate, offset), raw.sta, arrZone)
    const minutes = (candidate - stdUtc) / 60_000
    if (minutes < 0 || minutes > MAX_LEG_MINUTES) continue
    if (best === null || candidate < best) best = candidate
  }
  // Nothing plausible: keep the stated day rather than inventing one, and let
  // the leg show up as the oddity it is.
  return best ?? instantOf(raw.operatingDate, raw.sta, arrZone)
}

/**
 * An actual or estimated reading, which carries no date either.
 *
 * Anchored to its scheduled counterpart: a departure that slipped past
 * midnight reads 00:20 against a 23:50 schedule, and the nearest instant is
 * the one 30 minutes later rather than the one 23 hours earlier.
 */
function nearestReading(
  raw: RawLeg,
  clock: string,
  zone: { zone: string; rules: ZoneRules },
  anchor: number,
): number {
  let best = instantOf(raw.operatingDate, clock, zone)
  for (const offset of [1, -1, 2]) {
    const candidate = instantOf(addDays(raw.operatingDate, offset), clock, zone)
    if (Math.abs(candidate - anchor) < Math.abs(best - anchor)) best = candidate
  }
  return best
}

/** "2026-09-30" + 1 -> "2026-10-01". */
export function addDays(date: string, days: number): string {
  const [year, month, day] = date.split('-').map(Number)
  const shifted = new Date(Date.UTC(year, month - 1, day + days))
  return toDateString(shifted.getTime())
}

/** Unix ms -> "YYYY-MM-DD" in UTC. */
export function toDateString(utcMs: number): string {
  const date = new Date(utcMs)
  const month = date.getUTCMonth() + 1
  const day = date.getUTCDate()
  return `${date.getUTCFullYear()}-${month < 10 ? '0' : ''}${month}-${day < 10 ? '0' : ''}${day}`
}
